import { supabase } from './supabase';
import { ErrorDeApi } from './error-api';

/**
 * Alta de un negocio y entrada de su gente: crear el negocio, invitar y aceptar la invitación.
 *
 * Aquí «negocio» es el tenant y nada de esto es de plataforma: el API del Panel es `api-panel.ts`.
 */

interface DatosNegocio {
  /**
   * docs/08 §3 y §5: sin esto el servidor rechaza el alta con `TN_CONSENTIMIENTO_OBLIGATORIO`. No
   * tiene valor por defecto a propósito: la autorización se marca, no se asume.
   */
  autorizaDatos: boolean;
  nombre: string;
  prefijo: string;
  nombres: string;
  apellidos: string;
  docTipo: string;
  docNumero: string;
  telefono: string;
}

/**
 * El alta va siempre como persona natural: el RPC rechaza `juridica` hasta que exista facturación
 * electrónica. Los parámetros de empresa quedan en su valor por defecto.
 */
export async function crearNegocioRpc(d: DatosNegocio): Promise<string> {
  const { data, error, status } = await supabase.rpc('crear_negocio', {
    p_nombre: d.nombre,
    p_prefijo: d.prefijo,
    p_nombre_visible: `${d.nombres} ${d.apellidos}`.trim(),
    p_tipo_persona: 'natural',
    p_doc_tipo: d.docTipo,
    p_doc_numero: d.docNumero,
    p_telefono: d.telefono,
    p_nombres: d.nombres,
    p_apellidos: d.apellidos,
    p_autoriza_datos: d.autorizaDatos,
  });
  if (error) throw new ErrorDeApi(error, status);
  return data as string;
}

export interface DatosDelNegocio {
  nombre: string;
  nombres: string;
  apellidos: string;
  docTipo: string;
  docNumero: string;
  telefono: string;
}

/**
 * Corrige lo que se escribió en el alta: un solo guardado escribe negocio y membresía en una
 * transacción. Devuelve el nombre visible ya normalizado por el servidor.
 */
export async function actualizarNegocioRpc(negocioId: string, d: DatosDelNegocio): Promise<string> {
  const { data, error, status } = await supabase.rpc('actualizar_negocio', {
    p_negocio: negocioId,
    p_nombre: d.nombre,
    p_nombres: d.nombres,
    p_apellidos: d.apellidos,
    p_doc_tipo: d.docTipo,
    p_doc_numero: d.docNumero,
    p_telefono: d.telefono,
  });
  if (error) throw new ErrorDeApi(error, status);
  return data as string;
}

export async function crearInvitacionRpc(negocioId: string, correo: string, rol: string): Promise<string> {
  const { data, error, status } = await supabase.rpc('crear_invitacion', {
    p_negocio: negocioId,
    p_correo: correo.trim().toLowerCase(),
    p_rol: rol,
  });
  if (error) throw new ErrorDeApi(error, status);
  return data as string;
}

export interface InvitacionLeida {
  correo: string;
  rol: string;
  negocio: string;
}

/**
 * Lo que el token dice de sí mismo, sin sesión: a qué correo se le pide el código, con qué rol y a
 * qué negocio. Devuelve null si la invitación no está vigente (no existe, venció o ya se usó).
 */
export async function leerInvitacionRpc(token: string): Promise<InvitacionLeida | null> {
  const { data, error, status } = await supabase.rpc('leer_invitacion', { p_token: token });
  if (error) throw new ErrorDeApi(error, status);
  const filas = data as InvitacionLeida[];
  return filas[0] ?? null;
}

export async function aceptarInvitacionRpc(token: string, nombreVisible: string): Promise<string> {
  const { data, error, status } = await supabase.rpc('aceptar_invitacion', {
    p_token: token,
    p_nombre_visible: nombreVisible || null,
  });
  if (error) throw new ErrorDeApi(error, status);
  return data as string;
}

export interface PersonaDelEquipo {
  nombre: string;
  rol: string;
  soyYo: boolean;
}

/** Quiénes están en el negocio: solo nombre y rol, que cualquiera del equipo puede ver. */
export async function listarEquipo(negocioId: string): Promise<PersonaDelEquipo[]> {
  const { data, error, status } = await supabase.rpc('equipo_del_negocio', { p_negocio: negocioId });
  if (error) throw new ErrorDeApi(error, status);
  return ((data ?? []) as { nombre: string; rol: string; soy_yo: boolean }[]).map((f) => ({
    nombre: f.nombre,
    rol: f.rol,
    soyYo: f.soy_yo === true,
  }));
}

export interface InvitacionDelNegocio {
  id: string;
  correo: string;
  rol: string;
  creadoEn: string;
  expiraEn: string;
  aceptadaEn: string | null;
}

/** Las invitaciones del negocio que no se han anulado, la más reciente primero. */
export async function listarInvitaciones(negocioId: string): Promise<InvitacionDelNegocio[]> {
  const { data, error, status } = await supabase
    .from('invitaciones')
    .select('id, correo, rol, creado_en, expira_en, aceptada_en')
    .eq('negocio_id', negocioId)
    .is('eliminado_en', null)
    .order('creado_en', { ascending: false });
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []).map((f) => ({
    id: f.id as string,
    correo: f.correo as string,
    rol: f.rol as string,
    creadoEn: f.creado_en as string,
    expiraEn: f.expira_en as string,
    aceptadaEn: f.aceptada_en as string | null,
  }));
}

/** Anula una invitación que no se ha usado: su enlace deja de responder. */
export async function anularInvitacion(id: string): Promise<void> {
  const { error, status } = await supabase
    .from('invitaciones')
    .update({ eliminado_en: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new ErrorDeApi(error, status);
}
